/**
 * Client-side mirror of the backend's period-key arithmetic
 * (`backend/src/main/java/dev/hypermaple/period/PeriodKeys.java`).
 *
 * GMS resets daily at 00:00 UTC, weekly on Thursday 00:00 UTC and monthly on the 1st. Every
 * function here is UTC-only and pure — no `Date.now()` inside — so the checklist's week
 * navigation and countdowns can be unit tested against fixed dates and stay in lockstep with the
 * server's own key for the same instant.
 *
 * Period keys are represented as `YYYY-MM-DD` strings, matching the `LocalDate` JSON the API
 * sends/accepts.
 */

const WEEKLY_RESET_UTC_DAY = 4; // Date#getUTCDay(): 0 = Sunday ... 4 = Thursday

/** The UTC calendar date (`YYYY-MM-DD`) `date` falls on. */
export function isoDateUtc(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Parses an `YYYY-MM-DD` key into a `Date` at 00:00:00.000 UTC. */
export function parseIsoDate(iso: string): Date {
  const parts = iso.split('-').map(Number);
  const year = parts[0] ?? 0;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(Date.UTC(year, month - 1, day));
}

/** `iso` shifted by `days` (may be negative), still a `YYYY-MM-DD` key. */
export function addDaysIso(iso: string, days: number): string {
  const date = parseIsoDate(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return isoDateUtc(date);
}

/** The UTC calendar date containing `at`. */
export function dailyKey(at: Date): string {
  return isoDateUtc(at);
}

/**
 * The date of the most recent Thursday 00:00 UTC at or before `at` — mirrors
 * `PeriodKeys.weeklyKey`. A Thursday maps to itself.
 */
export function weeklyKey(at: Date): string {
  const iso = isoDateUtc(at);
  const daysSinceThursday = (parseIsoDate(iso).getUTCDay() - WEEKLY_RESET_UTC_DAY + 7) % 7;
  return addDaysIso(iso, -daysSinceThursday);
}

/** The UTC calendar month containing `at`, normalized to its 1st (mirrors `PeriodKeys.monthlyKey`). */
export function monthlyKey(at: Date): string {
  const iso = isoDateUtc(at);
  const date = parseIsoDate(iso);
  return isoDateUtc(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)));
}

/** The instant the next daily period starts, strictly after `from` (mirrors `PeriodKeys.nextReset`). */
export function nextDailyResetUtc(from: Date): Date {
  return new Date(
    Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + 1, 0, 0, 0, 0),
  );
}

/** The instant the next weekly (Thursday) period starts, strictly after `from`. */
export function nextWeeklyResetUtc(from: Date): Date {
  const thisWeek = parseIsoDate(weeklyKey(from));
  return new Date(
    Date.UTC(
      thisWeek.getUTCFullYear(),
      thisWeek.getUTCMonth(),
      thisWeek.getUTCDate() + 7,
      0,
      0,
      0,
      0,
    ),
  );
}

/** The instant the next monthly period starts, strictly after `from`. */
export function nextMonthlyResetUtc(from: Date): Date {
  const thisMonth = parseIsoDate(monthlyKey(from));
  return new Date(Date.UTC(thisMonth.getUTCFullYear(), thisMonth.getUTCMonth() + 1, 1, 0, 0, 0, 0));
}

export function formatRemaining(ms: number): string {
  const clamped = Math.max(0, ms);
  const totalSeconds = Math.floor(clamped / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

/** The 7 GMS-week dates (Thursday through Wednesday) starting at `weekKeyIso`. */
export function weekDates(weekKeyIso: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysIso(weekKeyIso, i));
}

/** `weekKeyIso` shifted by whole weeks (may be negative). */
export function shiftWeek(weekKeyIso: string, deltaWeeks: number): string {
  return addDaysIso(weekKeyIso, deltaWeeks * 7);
}

/** Short human label for a week, e.g. "Aug 13 – Aug 19, 2026". */
export function formatWeekRange(weekKeyIso: string): string {
  const start = parseIsoDate(weekKeyIso);
  const end = parseIsoDate(addDaysIso(weekKeyIso, 6));
  const startLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const endLabel = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `${startLabel} – ${endLabel}`;
}

/** Weekday label for a day-of-week column header, e.g. "Thu 14". */
export function formatDayColumn(iso: string): string {
  const date = parseIsoDate(iso);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  return `${weekday} ${date.getUTCDate()}`;
}
